import dayjs from "dayjs";
import { useCallback, useMemo, useRef, useState } from "react";

import { ResourceGroupEmployeeUsageLineChart } from "@components/Charts";
import {
  DashboardCell,
  DashboardChartFrame,
  DashboardGrid,
  DashboardWidget,
  DashboardWindowBar,
  dashboardRangeEndingToday,
  formatDashboardRangeLabel,
  type DashboardDateRange,
} from "@components/Dashboards/shell";
import {
  ApplicationDashboardFilterForm,
  ApplicationDashboardFilterFormRef,
  ApplicationDashboardFilterFormValues,
} from "@components/Forms";
import {
  ResourceGroupAuthenticatedUsersStat,
  ResourceGroupTotalUsersStat,
  ResourceGroupUsageTimeStat,
} from "@components/Stats";
import { ResourceGroupEmployeesTable } from "@components/Tables/ResourceGroupEmployeesTable/ResourceGroupEmployeesTable";

import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel";

import { useApplicationDashboardDataQuery } from "./ApplicationDashboard.g";

type Props = {
  applicationId: string;
  clientId: string;
};

export const ApplicationDashboard = ({ applicationId, clientId }: Props) => {
  const [filterValues, setFilterValues] =
    useState<ApplicationDashboardFilterFormValues>({
      date_Gte: undefined,
      date_Lte: undefined,
    });

  const filterFormRef = useRef<ApplicationDashboardFilterFormRef>(null);

  const areFiltersActive = useMemo(() => {
    return Boolean(filterValues.date_Gte || filterValues.date_Lte);
  }, [filterValues]);

  const [dateRange, setDateRange] = useState<DashboardDateRange>(() =>
    dashboardRangeEndingToday(30),
  );

  const windowLabel = formatDashboardRangeLabel(dateRange);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const handleApplyFilters = useCallback((): boolean => {
    if (!filterFormRef.current?.applyIfValid()) {
      return false;
    }
    const currentFormValues = filterFormRef.current.getCurrentValues();
    setFilterValues(currentFormValues);
    setDateRange([
      currentFormValues.date_Gte
        ? dayjs(currentFormValues.date_Gte).toDate()
        : null,
      currentFormValues.date_Lte
        ? dayjs(currentFormValues.date_Lte).toDate()
        : null,
    ]);
    return true;
  }, []);

  const handleFilterChange = useCallback(
    (newValues: ApplicationDashboardFilterFormValues) => {
      setFilterValues(newValues);
      setIsFilterPanelOpen(false);
      setDateRange([
        newValues.date_Gte ? dayjs(newValues.date_Gte).toDate() : null,
        newValues.date_Lte ? dayjs(newValues.date_Lte).toDate() : null,
      ]);
    },
    [],
  );

  const handleFiltersReset = useCallback(() => {
    setFilterValues({
      date_Gte: undefined,
      date_Lte: undefined,
    });
    setDateRange(dashboardRangeEndingToday(30));
  }, []);

  const { data, isLoading } = useApplicationDashboardDataQuery({
    clientId,
    clientApplicationId: applicationId,
  });

  const resourceGroupId = data?.clientApplication?.resource?.resourceGroup?.id;
  const stats = data?.clientApplication?.resourceGroupApplications?.stats;

  if (isLoading || !resourceGroupId) {
    return null;
  }

  return (
    <>
      <DashboardGrid>
        <DashboardCell span={4}>
          <ResourceGroupUsageTimeStat
            prominent
            lastSessionTime={stats?.lastSessionTime}
          />
        </DashboardCell>
        <DashboardCell span={4}>
          <ResourceGroupTotalUsersStat
            prominent
            totalUsers={stats?.totalEmployees}
          />
        </DashboardCell>
        <DashboardCell span={4}>
          <ResourceGroupAuthenticatedUsersStat
            prominent
            totalEmployeesAuthenticated={stats?.totalEmployeesAuthenticated}
          />
        </DashboardCell>

        <DashboardCell span={12}>
          <DashboardWindowBar
            label={windowLabel}
            actions={
              <IconButton
                size="sm"
                icon="faFilter"
                onClick={() => setIsFilterPanelOpen(true)}
                outline="sm"
                showBubble={areFiltersActive}
                tooltip="Show filters"
              />
            }
          />
        </DashboardCell>

        <DashboardCell span={12}>
          <DashboardWidget
            title="Application usage by users"
            definition={windowLabel}
          >
            <DashboardChartFrame>
              <ResourceGroupEmployeeUsageLineChart
                clientApplicationId={applicationId}
                daysToShow={30}
                startDate={dateRange[0] || undefined}
                endDate={dateRange[1] || undefined}
              />
            </DashboardChartFrame>
          </DashboardWidget>
        </DashboardCell>

        <DashboardCell span={12}>
          <DashboardWidget
            title="Users for selected range"
            definition={windowLabel}
          >
            <ResourceGroupEmployeesTable
              clientId={clientId}
              clientApplicationId={applicationId}
              dateRange={dateRange}
            />
          </DashboardWidget>
        </DashboardCell>
      </DashboardGrid>

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={areFiltersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title="Application Dashboard Filters"
      >
        <ApplicationDashboardFilterForm
          ref={filterFormRef}
          onFilterChange={handleFilterChange}
          values={filterValues}
          errorsAPI={undefined}
        />
      </SlideInPanel>
    </>
  );
};
